import { useEffect, useMemo, useState } from 'react';
import {
  AlertCircle,
  CheckCircle,
  Clapperboard,
  Film,
  KeyRound,
  Loader2,
  Mic2,
  RefreshCw,
  Save,
  Trash2,
  Video,
} from 'lucide-react';
import {
  adProductionIntegrationsApi,
  type AdProductionIntegrationStatus,
  type AdProductionProvider,
} from '../lib/api';
import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { useConfirmDialog } from '../contexts/ConfirmDialogContext';

type ProviderForm = {
  apiKey: string;
  isActive: boolean;
};

const providerIcons: Record<AdProductionProvider, typeof Video> = {
  heygen: Clapperboard,
  higgsfield: Video,
  elevenlabs: Mic2,
  kieai: Film,
};

function emptyForm(): ProviderForm {
  return {
    apiKey: '',
    isActive: true,
  };
}

function formFromStatus(status: AdProductionIntegrationStatus): ProviderForm {
  return {
    apiKey: '',
    isActive: status.isActive || !status.connected,
  };
}

function errorMessage(error: unknown, fallback: string): string {
  if (typeof error === 'object' && error && 'response' in error) {
    const response = (error as { response?: { data?: { error?: string; message?: string } } }).response;
    return response?.data?.error || response?.data?.message || fallback;
  }
  return error instanceof Error ? error.message : fallback;
}

function formatDate(value: string | null): string {
  if (!value) return 'Brak danych';
  return new Date(value).toLocaleString('pl-PL', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function AdProductionIntegrationsPage() {
  const confirm = useConfirmDialog();
  const [statuses, setStatuses] = useState<AdProductionIntegrationStatus[]>([]);
  const [forms, setForms] = useState<Record<string, ProviderForm>>({});
  const [loading, setLoading] = useState(true);
  const [savingProvider, setSavingProvider] = useState<AdProductionProvider | null>(null);
  const [disconnectingProvider, setDisconnectingProvider] = useState<AdProductionProvider | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const providersById = useMemo(() => (
    Object.fromEntries(statuses.map(status => [status.provider, status])) as Record<AdProductionProvider, AdProductionIntegrationStatus>
  ), [statuses]);

  const loadStatus = async () => {
    try {
      setError(null);
      const res = await adProductionIntegrationsApi.getStatus();
      const nextStatuses = res.data.data;
      setStatuses(nextStatuses);
      setForms(Object.fromEntries(nextStatuses.map(status => [status.provider, formFromStatus(status)])));
    } catch (err) {
      setError(errorMessage(err, 'Nie udało się pobrać statusu integracji produkcji reklam.'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadStatus();
  }, []);

  const updateForm = (provider: AdProductionProvider, patch: Partial<ProviderForm>) => {
    setForms(prev => ({
      ...prev,
      [provider]: {
        ...(prev[provider] || emptyForm()),
        ...patch,
      },
    }));
  };

  const handleSave = async (provider: AdProductionProvider) => {
    const form = forms[provider] || emptyForm();
    setSavingProvider(provider);
    setMessage(null);
    setError(null);
    try {
      const res = await adProductionIntegrationsApi.save(provider, {
        apiKey: form.apiKey.trim() || undefined,
        isActive: form.isActive,
      });
      setStatuses(prev => prev.map(status => (
        status.provider === provider ? res.data.data : status
      )));
      setForms(prev => ({
        ...prev,
        [provider]: formFromStatus(res.data.data),
      }));
      setMessage(`${res.data.data.name} został zapisany.`);
    } catch (err) {
      setError(errorMessage(err, 'Nie udało się zapisać integracji.'));
    } finally {
      setSavingProvider(null);
    }
  };

  const handleDisconnect = async (provider: AdProductionProvider) => {
    const status = providersById[provider];
    const confirmed = await confirm({
      title: `Odłączyć ${status?.name || 'integrację'}?`,
      message: 'EcomFlow usunie zapisany klucz API i przestanie oznaczać tę usługę jako dostępną w pipeline reklam wideo.',
      confirmLabel: 'Odłącz',
      cancelLabel: 'Anuluj',
      danger: true,
    });
    if (!confirmed) return;

    setDisconnectingProvider(provider);
    setMessage(null);
    setError(null);
    try {
      await adProductionIntegrationsApi.disconnect(provider);
      await loadStatus();
      setMessage(`${status?.name || 'Integracja'} została odłączona.`);
    } catch (err) {
      setError(errorMessage(err, 'Nie udało się odłączyć integracji.'));
    } finally {
      setDisconnectingProvider(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-accent-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-text-primary">Produkcja reklam AI</h1>
          <p className="text-text-secondary mt-2">
            HeyGen dla UGC, Higgsfield dla B-rolli i ElevenLabs dla lektora w pipeline reklam wideo.
          </p>
        </div>
        <Button
          variant="secondary"
          size="sm"
          onClick={loadStatus}
          leftIcon={<RefreshCw />}
        >
          Odśwież
        </Button>
      </div>

      {message && <Alert variant="success" title="Gotowe" onClose={() => setMessage(null)}>{message}</Alert>}
      {error && <Alert variant="danger" title="Błąd" onClose={() => setError(null)}>{error}</Alert>}

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        {statuses.map(status => {
          const Icon = providerIcons[status.provider] || Video;
          const form = forms[status.provider] || emptyForm();
          const saving = savingProvider === status.provider;
          const disconnecting = disconnectingProvider === status.provider;

          return (
            <div key={status.provider} className="card space-y-5">
              <div className="flex items-start gap-4">
                <div className="p-3 rounded-xl bg-gradient-to-br from-accent-primary to-accent-secondary">
                  <Icon className="w-6 h-6 text-dark-900" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-3">
                    <h2 className="text-xl font-semibold text-text-primary">{status.name}</h2>
                    <span className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs ${
                      status.connected && status.isActive
                        ? 'bg-success/15 text-success'
                        : 'bg-surface-hover text-text-muted'
                    }`}>
                      {status.connected && status.isActive ? <CheckCircle className="w-3.5 h-3.5" /> : <AlertCircle className="w-3.5 h-3.5" />}
                      {status.connected && status.isActive ? 'Aktywna' : 'Nieaktywna'}
                    </span>
                  </div>
                  <p className="text-sm text-text-secondary mt-2">{status.purpose}</p>
                  <p className="text-xs text-text-muted mt-2">Ostatnia aktualizacja: {formatDate(status.updatedAt)}</p>
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-text-secondary mb-1.5">
                    API key
                  </label>
                  <div className="relative">
                    <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" />
                    <input
                      type="password"
                      value={form.apiKey}
                      onChange={e => updateForm(status.provider, { apiKey: e.target.value })}
                      placeholder={status.hasApiKey ? 'Zapisany - wpisz nowy, aby zmienić' : 'Wklej API key'}
                      className="w-full pl-10 pr-4 py-3 bg-surface-hover/80 border border-border rounded-xl text-text-primary text-sm focus:outline-none focus:border-accent-primary/50 placeholder:text-text-primary/25"
                    />
                  </div>
                </div>

                <label className="flex items-center gap-2 text-sm text-text-secondary cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.isActive}
                    onChange={e => updateForm(status.provider, { isActive: e.target.checked })}
                    className="rounded border-border-strong bg-surface-hover text-accent-primary focus:ring-accent-primary/30"
                  />
                  Aktywna w pipeline reklam
                </label>

              </div>

              <div className="flex items-center justify-between gap-3 pt-1">
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => handleSave(status.provider)}
                  disabled={saving || disconnecting}
                  leftIcon={saving ? <Loader2 className="animate-spin" /> : <Save />}
                >
                  Zapisz
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => handleDisconnect(status.provider)}
                  disabled={!status.connected || saving || disconnecting}
                  leftIcon={disconnecting ? <Loader2 className="animate-spin" /> : <Trash2 />}
                  className="text-danger hover:text-danger"
                >
                  Odłącz
                </Button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
